import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addYears,
  formatPriceCurrency,
  getCurrency,
  showError,
  validate,
} from '@bepro-travel/fe.utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookingModel,
  Credit2000PaymentInfo,
  Pax,
  RoomsClass,
} from '../../ts/interfaces/OphirBook';
import { bookComplete } from '../../service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { useRecaptcha } from '@bepro-travel/fe.hooks';
import { Checkbox, FormGroup, FormGroupDatepicker } from '@bepro-travel/fe.ui';
import { getPackagePassengerValidation } from '../../utils';
import BookPayment from './BookPayment';

interface OrderBookProps {
  bookingModel: BookingModel;
  onClickBook: () => void;
}

const OrderBook: FC<OrderBookProps> = ({ bookingModel, ...props }) => {
  const [t] = useTranslation() as unknown as [(key: string) => string];
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [roomsState, setRoomsState] = useState<RoomsClass[]>();
  const { handleRecapcha } = useRecaptcha();

  useEffect(() => {
    if (bookingModel) {
      let startIndex = 0;
      setRoomsState(
        bookingModel.roomsClasses.map((room) => {
          return {
            ...room,
            paxes: bookingModel.paxes.slice(startIndex, room.room?.[0].numofPax || 1),
          };
        })
      );
    }
  }, [bookingModel]); // eslint-disable-line

  const onClickSend = async () => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);

      const recaptchaToken = await handleRecapcha();
      if (!recaptchaToken) {
        setIsLoading(false);
        return;
      }
      const paxesState = roomsState!
        .reduce((acc: Pax[], room) => {
          return [...acc, ...room.paxes];
        }, [])
        .map((pax) => {
          return {
            ...pax,
            gender: 'M',
          };
        });

      const body = {
        bookingModel: {
          ...bookingModel,
          paxes: paxesState,
          paymentSettings: bookingModel.credit2000PaymentInfo,
        },
        searchToken: urlParams.get('searchToken'),
        creditCard: false,
        recaptchaToken,
      };

      props.onClickBook();

      const response = await bookComplete(body);
      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          showError(response.errorMessage);
        } else {
          sessionStorage.removeItem('screenshotsOphir');
          sessionStorage.setItem('ophirSuccessOrder', JSON.stringify(response));

          const lang = localStorage.getItem('language') || 'he';
          const pathname = lang === 'he' ? '/tour/success' : '/tourru/success';
          navigate(pathname);
        }
      }

      setIsLoading(false);
    }
  };
  const onClickSubmit = async (credit2000: Credit2000PaymentInfo) => {
    setIsSubmitted(true);

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) {
      setIsLoading(false);
      return;
    }
    if (isValid()) {
      setIsLoading(true);
      const paxesState = roomsState!
        .reduce((acc: Pax[], room) => {
          return [...acc, ...room.paxes];
        }, [])
        .map((pax) => {
          return {
            ...pax,
            gender: 'M',
          };
        });
      const body = {
        bookingModel: {
          ...bookingModel,
          paxes: paxesState,
          paymentSettings: bookingModel.credit2000PaymentInfo,
        },
        recaptchaToken,
        searchToken: urlParams.get('searchToken'),
        creditCard: true,
      };
      props.onClickBook();

      const response = await bookComplete(body);
      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          showError(response.errorMessage);
        } else {
          sessionStorage.removeItem('screenshotsOphir');
          sessionStorage.setItem('ophirPaymentOrder', JSON.stringify(response));

          const lang = localStorage.getItem('language') || 'he';
          const pathname = lang === 'he' ? '/tour/payment' : '/tourru/payment';
          navigate(pathname);
        }
      }

      setIsLoading(false);
    }
  };

  const isValid = () => {
    let result = true;
    const paxesState = roomsState?.reduce((acc: Pax[], room) => {
      return [...acc, ...room.paxes];
    }, []);
    paxesState?.forEach((pax, index) => {
      const validation = getPackagePassengerValidation(index);
      if (!validate(validation, pax)) {
        result = false;
      }
    });
    return result && termsAccepted;
  };

  const onChangeField = (pax: Pax, roomIndex: number, index: number) => {
    const newPaxesState = [...roomsState!];
    newPaxesState[roomIndex].paxes[index] = pax;

    setRoomsState(newPaxesState);
  };

  return (
    <div className="details book">
      <div className="book-form default-shadow">
        <div className="title-underline text-lg mb-5">{t('passenger.details')}</div>
        <div className="color-danger mb-10 text-xsm">{t('only_english_input')}</div>
        {roomsState?.map((room, roomIndex) => (
          <div key={roomIndex} className="book-form-room-wr">
            <div className="text-cap text-contrast text-bold text-md mb-10">
              {room.paxes.map((_pax, paxIndex) => (
                <FontAwesomeIcon icon={faUser as any} key={paxIndex} />
              ))}
              <span className="mr-5"> {room.class.desc?.toLowerCase()}</span>
            </div>
            <div>
              {room.paxes.map((pax, index) => (
                <div className="book-form-room" key={index}>
                  <div className="title text-st mb-15">
                    <span className="mr-5">
                      {t('passenger.guest')} {index + 1}
                    </span>
                  </div>
                  <div className="form-wrapper row">
                    <FormGroup
                      onChange={(firstName) =>
                        onChangeField({ ...pax, firstName }, roomIndex, index)
                      }
                      value={pax.firstName}
                      label={t('passenger.fn')}
                      className="col-md-6 col-12"
                      validation={getPackagePassengerValidation(index).firstName}
                      isSubmitted={isSubmitted}
                    />
                    <FormGroup
                      onChange={(lastName) => onChangeField({ ...pax, lastName }, roomIndex, index)}
                      value={pax.lastName}
                      label={t('passenger.ln')}
                      className="col-md-6 col-12"
                      validation={getPackagePassengerValidation(index).lastName}
                      isSubmitted={isSubmitted}
                    />
                  </div>
                  <div className="form-wrapper row">
                    {index === 0 && roomIndex === 0 && (
                      <Fragment>
                        <FormGroup
                          onChange={(phone) => onChangeField({ ...pax, phone }, roomIndex, index)}
                          value={pax.phone}
                          label={t('passenger.phone')}
                          className="col-md-6 col-12"
                          validation={getPackagePassengerValidation(index).phone}
                          isSubmitted={isSubmitted}
                        />
                        <FormGroup
                          onChange={(email) => onChangeField({ ...pax, email }, roomIndex, index)}
                          value={pax.email}
                          label={t('passenger.email')}
                          className="col-md-6 col-12"
                          validation={getPackagePassengerValidation(index).email}
                          isSubmitted={isSubmitted}
                        />
                      </Fragment>
                    )}
                  </div>
                  <div className="form-wrapper row">
                    <FormGroup
                      onChange={(email2) => onChangeField({ ...pax, email2 }, roomIndex, index)}
                      value={pax.email2}
                      label={t('passenger.id_number')}
                      className="col-md-6 col-12"
                      validation={getPackagePassengerValidation(index).email2}
                      isSubmitted={isSubmitted}
                      allowOnlyNumbers
                    />
                    <FormGroup
                      onChange={(passportNumber) =>
                        onChangeField({ ...pax, passportNumber }, roomIndex, index)
                      }
                      value={pax.passportNumber}
                      label={t('passenger.passport')}
                      className="col-md-6 col-12"
                      validation={getPackagePassengerValidation(index).passportNumber}
                      isSubmitted={isSubmitted}
                    />
                    <FormGroupDatepicker
                      onChange={(passportValidTill) =>
                        onChangeField({ ...pax, passportValidTill }, roomIndex, index)
                      }
                      value={pax.passportValidTill}
                      label={t('passenger.passport_valid_till')}
                      className="col-md-6 col-12"
                      validation={getPackagePassengerValidation(index).passportValidTill}
                      isSubmitted={isSubmitted}
                      maxDate={addYears(10)}
                      minDate={addYears(0)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <Checkbox
          checked={termsAccepted}
          onChange={setTermsAccepted}
          label={t('passenger.confirm')}
          htmlLabel={
            <a className="btn-lnk" href="/pages/terms" target="_blank">
              {t('passenger.read_more')}
            </a>
          }
          className="mb-20"
          hasError={isSubmitted && !termsAccepted}
        />
        <div className="mb-30 text-bold text-md">
          {t('total_price')}:{' '}
          {formatPriceCurrency(bookingModel.totalPrice, getCurrency(bookingModel.currency))}
        </div>
        <BookPayment
          onClickSend={onClickSend}
          onClickSubmit={onClickSubmit}
          disabled={isLoading}
          privileges={bookingModel.privilegesInfo}
          credit2000={bookingModel.credit2000PaymentInfo}
          isFormValid={isValid()}
        />
      </div>
    </div>
  );
};

export default OrderBook;
